import React, { useState } from 'react';
import { UserBoxIcon } from '../../AssetComponents/Images';
import placeholderPng from '../../assets/images/placeholder.png';
import logoForQRcode from '../../assets/images/logoForQRcode.svg';

/**
 * ImageWithFallback Component
 * Displays an image with fallback to placeholder when image fails to load
 * Uses the same fallback image as the onboarding screen
 */
export const ImageWithFallback = ({ 
  src, 
  alt = "Image", 
  className = "", 
  fallbackSrc = logoForQRcode,
  showUserIcon = false,
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  // If image failed to load, show fallback
  if (imageError) {
    return (
      <div className={`relative ${className}`} {...props}>
        <img
          src={fallbackSrc}
          alt={alt}
          className={`w-full h-full object-cover ${className}`}
          onError={(e) => {
            // If even the fallback fails, show a solid background with icon
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        {showUserIcon && (
          <div 
            className="absolute inset-0 bg-gray-100 flex items-center justify-center"
            style={{ display: 'none' }}
          >
            <UserBoxIcon className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} {...props}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        } ${className}`}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
    </div>
  );
};

/**
 * AvatarWithFallback Component
 * Specialized component for user avatars with fallback
 */
export const AvatarWithFallback = ({ 
  src, 
  alt = "Avatar", 
  className = "", 
  size = "md",
  ...props 
}) => {
  const sizeClasses = {
    xs: "w-6 h-6",
    sm: "w-8 h-8", 
    md: "w-10 h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16"
  };

  const iconSizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5", 
    lg: "w-6 h-6",
    xl: "w-8 h-8"
  };

  return (
    <ImageWithFallback
      src={src}
      alt={alt}
      className={`${sizeClasses[size]} rounded-full ${className}`}
      showUserIcon={true}
      {...props}
    />
  );
};

/**
 * PropertyImageWithFallback Component
 * Specialized component for property images with fallback
 */
export const PropertyImageWithFallback = ({ 
  src, 
  alt = "Property", 
  className = "", 
  ...props 
}) => {
  return (
    <ImageWithFallback
      src={src}
      alt={alt}
      className={`rounded-lg ${className}`}
      fallbackSrc={logoForQRcode}
      {...props}
    />
  );
};

/**
 * NotificationImageWithFallback Component
 * Specialized component for notification images with fallback
 */
export const NotificationImageWithFallback = ({ 
  src, 
  alt = "Notification", 
  className = "", 
  size = "md",
  ...props 
}) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12"
  };

  return (
    <ImageWithFallback
      src={src}
      alt={alt}
      className={`${sizeClasses[size]} rounded-full ${className}`}
      showUserIcon={true}
      {...props}
    />
  );
};
